import { Divider } from 'antd';
import { useState } from 'react';
import { EpicIcon } from '../../../assets/CustomIcons/EpicIcon';
import { capitalizeFirstLetter } from '../../../common/utils';
import colorTokens from '../../../constants/design_system/colors';
import { TaskType, TASK_TYPE_ORDER } from '../../../types/TaskType';
import BaseDropdown from './BaseDropdown';
import NoDragWrapper from './NoDragWrapper';

export type TaskTypeDropdownProps = {
  type: TaskType;
  size: 'small' | 'medium' | 'large';
  onSelect?: (type: TaskType) => void;
  onBlur: () => void;
  disabled?: boolean;
  allowEpic?: boolean;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
};

const TaskTypeDropdown = (props: TaskTypeDropdownProps) => {
  // Shared ordering with the filter panel (#2443) — see TASK_TYPE_ORDER.
  const taskTypes = TASK_TYPE_ORDER.filter((type) => props.allowEpic || type !== TaskType.epic);

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const onSelect = (value: TaskType) => {
    if (props.onSelect) {
      props.onSelect(value);
    }
    isDropdownOpen && setDropdownOpen(false);
  };

  return (
    <NoDragWrapper className="h-full">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isDropdownOpen}
        onOpenChange={(open) => {
          setDropdownOpen(open);
          if (!open) {
            props.onBlur();
          }
        }}
        disabled={props.disabled}
        placement={props.position || 'bottomLeft'}
        dropdownRender={(menu) => (
          <div className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg p-2">
            {taskTypes.map((type) => (
              <div key={type}>
                <TaskTypeLabel type={type} size="large" onClick={() => onSelect(type)} />
                {type === TaskType.epic && <Divider className="my-0.5 bg-neutral-300" />}
              </div>
            ))}
          </div>
        )}
      >
        <div
          className="h-full flex items-center"
          style={{ outline: isDropdownOpen ? `2px solid ${colorTokens.green[300]}` : 'none' }}
        >
          <TaskTypeLabel type={props.type} size={props.size} disabled={props.disabled} />
        </div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

const colorMap: { [key in TaskType]: { background: string; border: string } } = {
  [TaskType.epic]: { background: colorTokens.purple[400], border: colorTokens.purple[300] },
  [TaskType.bug]: { background: colorTokens.red[500], border: colorTokens.red[300] },
  [TaskType.feature]: { background: colorTokens.orange[600], border: colorTokens.orange[300] },
  [TaskType.story]: { background: colorTokens.taskonioGreen[700], border: colorTokens.taskonioGreen[300] },
  [TaskType.task]: { background: colorTokens.blue[600], border: colorTokens.blue[300] },
  [TaskType.improvement]: { background: colorTokens.yellow[500], border: colorTokens.yellow[300] },
  [TaskType.idea]: { background: colorTokens.neutral[800], border: colorTokens.neutral[300] },
  [TaskType.experiment]: { background: colorTokens.purple[400], border: colorTokens.purple[300] },
};

export const TaskTypeLabel = (props: {
  type: TaskType;
  size: 'small' | 'medium' | 'large';
  onClick?: () => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={`${props.size === 'large' ? 'w-[180px] py-1' : ''} h-full px-1 flex items-center gap-2 ${
        props.onClick ? `${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} hover:bg-neutral-200` : ''
      }`}
      onClick={props.onClick}
    >
      <div
        className="h-4 w-4 border border-solid justify-center items-center flex"
        style={{
          backgroundColor: colorMap[props.type].background,
          borderColor: colorMap[props.type].border,
          borderRadius: '4px',
        }}
      >
        {props.type === TaskType.epic && <EpicIcon className="text-white" />}
        {props.type !== TaskType.epic && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
      </div>
      {props.size !== 'small' && (
        <div className="py-1 text-neutral-900 text-sm font-normal leading-tight hover:text-brand-grey-dark">
          {capitalizeFirstLetter(props.type)}
        </div>
      )}
    </div>
  );
};

export default TaskTypeDropdown;
